import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useReviewApi } from "../../api/reviews";
import { formatDateTimeWithAMPM } from "../../utils/dateFormat";

type Review = {
  rating: number;
  comment?: string | null;
  createdAt: string;
  reviewerName?: string | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  companyUserId: string; // <— b.UserId from your bid object
};

const Star: React.FC<{ filled: boolean }> = ({ filled }) => (
  <span aria-hidden className={filled ? "text-yellow-600" : "text-gray-300"}>
    ★
  </span>
);

const PreviewReviewsModal: React.FC<Props> = ({
  open,
  onClose,
  companyUserId,
}) => {
  const { getCompanyReviews } = useReviewApi();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<Review[]>([]);
  const [summary, setSummary] = useState<{ average: number; count: number }>({
    average: 0,
    count: 0,
  });

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const data = await getCompanyReviews(companyUserId, 0, 25);
        if (cancelled) return;
        setSummary(data.summary);
        setItems(data.items);
      } catch {
        if (!cancelled) {
          setSummary({ average: 0, count: 0 });
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, companyUserId, getCompanyReviews]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 translate-y-2 scale-95"
            enterTo="opacity-100 translate-y-0 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 translate-y-0 scale-100"
            leaveTo="opacity-0 translate-y-2 scale-95"
          >
            <Dialog.Panel className="w-full max-w-lg rounded-xl bg-white shadow-lg ring-1 ring-gray-200">
              <div className="px-5 pt-5 pb-3 border-b">
                <Dialog.Title className="text-lg font-semibold">
                  Company reviews
                </Dialog.Title>
                <div className="mt-1 text-sm text-gray-600 flex items-center gap-2">
                  <span className="inline-flex items-center">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} filled={i <= Math.round(summary.average)} />
                    ))}
                  </span>
                  <span>
                    {summary.average.toFixed(1)} · {summary.count} review
                    {summary.count === 1 ? "" : "s"}
                  </span>
                </div>
              </div>

              <div className="p-5 max-h-[60vh] overflow-auto">
                {loading && (
                  <div className="text-sm text-gray-500">Loading…</div>
                )}
                {!loading && items.length === 0 && (
                  <div className="text-sm text-gray-500">No reviews yet.</div>
                )}
                <ul className="space-y-4">
                  {items.map((r, idx) => (
                    <li key={idx} className="rounded border p-3">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="inline-flex">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <Star key={i} filled={i <= r.rating} />
                          ))}
                        </span>
                        <span className="text-gray-500">
                          {formatDateTimeWithAMPM(r.createdAt)}
                          {r.reviewerName ? ` · ${r.reviewerName}` : ""}
                        </span>
                      </div>
                      {r.comment && (
                        <p className="mt-2 text-sm text-gray-800 whitespace-pre-wrap">
                          {r.comment}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="px-5 py-3 bg-gray-50 rounded-b-xl flex justify-end">
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-md text-sm border hover:bg-gray-100"
                >
                  Close
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default PreviewReviewsModal;
